import { useQuery } from '@apollo/client';
import { Box, DialogActions, styled } from '@mui/material';
import { BlockContainer } from 'components/ChainMapping/Forms/styles';
import { EFilterType } from 'components/Filter/filter.types';
import { Loader } from 'components/Forms';
import { DialogInner } from 'components/Partners/Partners.styles';
import { InlineHelperText } from 'components/Product/InlineHelperText/InlineHelperText';
import { ErrorState, FlexBox } from 'components/Structure';
import { useDialog } from 'components/hooks';
import { DialogDefault, ThemeButton } from 'designSystem';
import useSites from 'hooks/useSites';
import React, { FC, useMemo, useState } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import {
  ESiteType,
  IClusterSite,
  SiteViewOption,
  TSiteTypesWithoutCluster,
} from 'types/site.types';
import useSiteMutations from '../hooks/useSiteMutations';
import ManageSiteSummary, { TManageSiteErrors } from './ManageSiteSummary';
import SiteSelection from './SiteSelection';
import { GET_SITE } from 'graphql/secure/queries/site.queries';

interface IManageSiteDialogProps extends IDefaultDialogProps {
  site: TSiteTypesWithoutCluster;
}

const StyledBlockContainer = styled(BlockContainer)({
  paddingTop: 0,
  height: '100%',
});

const ManageSiteDialog: FC<IManageSiteDialogProps> = ({ open, site, onClose }) => {
  const [selectedClusterId, setSelectedClusterId] = useState<string | undefined>(
    site.siteClusterId
  );

  const { openDialog } = useDialog();
  const { updateSite, loading: loadingMutation } = useSiteMutations();

  const { data: selectedSiteClusterData, loading: selectedSiteClusterLoading } = useQuery<{
    site: IClusterSite;
  }>(GET_SITE, {
    variables: { id: site.siteClusterId },
    skip: !site.siteClusterId,
  });
  const {
    sites,
    loading,
    hasNextPage,
    error,
    searchTerm,
    filters,
    handlePageEndReached,
    handleSearchTermChange,
    handleFilterChange,
    handleFilterReset,
  } = useSites({
    skipCache: true,
    skipUseQueryParams: true,
    defaultViewOption: SiteViewOption.CLUSTER,
    defaultSiteTypes: [ESiteType.CLUSTER],
  });

  const selectedCluster: IClusterSite | undefined = useMemo(() => {
    if (!selectedClusterId) {
      return undefined;
    }

    if (
      selectedClusterId &&
      selectedClusterId === selectedSiteClusterData?.site.id &&
      selectedSiteClusterData?.site.siteType === ESiteType.CLUSTER
    ) {
      return selectedSiteClusterData?.site;
    } else {
      const matchedSite = sites.find(s => s.id === selectedClusterId);
      return matchedSite?.siteType === ESiteType.CLUSTER ? matchedSite : undefined;
    }
  }, [selectedSiteClusterData, sites, selectedClusterId]);

  const firstSiteOfSelectedCluster = useMemo(
    () => selectedCluster?.sites.edges?.[0]?.node,
    [selectedCluster]
  );

  const errors = useMemo(() => {
    // Enable button if no sites are selected
    if (!selectedCluster) return {};

    let errors = {} as TManageSiteErrors;

    // Check if all sites have the same siteType
    if (selectedCluster.sitesType && selectedCluster.sitesType !== site.siteType) {
      errors = { ...errors, siteType: 'Invalid site type' };
    }

    // Check if all sites have the same raw material
    if (
      selectedCluster.sitesType === ESiteType.FARM &&
      site.siteType === ESiteType.FARM &&
      site.rawMaterial.id !== firstSiteOfSelectedCluster?.rawMaterial.id
    ) {
      errors = { ...errors, rawMaterial: 'Invalid raw material' };
    }

    // Check if all sites are in the same country
    if (selectedCluster.countryCode && selectedCluster.countryCode !== site.countryCode) {
      errors = { ...errors, country: 'Invalid country' };
    }

    return errors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCluster, firstSiteOfSelectedCluster]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleChangeCluster = async () => {
    await updateSite({
      variables: {
        id: site.id,
        input: {
          siteType: site.siteType,
          siteClusterId: selectedClusterId || null,
        },
      },
    });
    onClose?.();
  };

  const handleRemoveFromCluster = async () => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Remove site from cluster',
        text: 'Are you sure you want to remove this site from the cluster? It will still be available in the list of sites.',
        itemTitle: site.title,
        displayCloseButton: true,
        submitText: 'Remove from cluster',
        onSubmit: async () => {
          await updateSite({
            variables: {
              id: site.id,
              input: {
                siteType: site.siteType,
                siteClusterId: null,
              },
            },
          });
          onClose?.();
        },
        onCancel: () => undefined,
        onClose: () => undefined,
      },
    });
  };

  return (
    <DialogDefault
      // If the site is already in a cluster, show the "Change site cluster" title
      // If the site is not in a cluster, show the "Add site to cluster" title
      title={site.siteClusterId ? 'Change site cluster' : 'Add site to cluster'}
      onClose={onClose}
      open={open}
      fullWidth
      iconName="site-cluster-manage"
      maxWidth="xl"
      background="#fff"
    >
      <DialogInner container minHeight={600}>
        {loading || selectedSiteClusterLoading ? (
          <Loader />
        ) : error ? (
          <ErrorState />
        ) : (
          <>
            <StyledBlockContainer container z-index={0}>
              <ManageSiteSummary
                site={site}
                requiredSiteType={site.siteType}
                requiredRawMaterialTitle={
                  site.siteType === ESiteType.FARM ? site?.rawMaterial?.title : undefined
                }
                requiredLocationName={site?.locationName}
                errors={errors}
              />

              <SiteSelection
                singleSelect
                sites={sites}
                selectedSiteIds={selectedClusterId ? [selectedClusterId] : []}
                // Workaround to always show the cluster sites
                filters={filters.filter(({ type }) => type !== EFilterType.SITE_TYPE)}
                hasNextPage={hasNextPage}
                loading={loading}
                searchTerm={searchTerm}
                onSearchTermChange={handleSearchTermChange}
                onSelectionChange={siteIds =>
                  // Workaround to get the last added site id into the single select state
                  setSelectedClusterId(siteIds.length ? siteIds[siteIds.length - 1] : undefined)
                }
                onPageEndReached={handlePageEndReached}
                onFilterChange={handleFilterChange}
                onFilterReset={handleFilterReset}
              />
            </StyledBlockContainer>
          </>
        )}
      </DialogInner>

      <DialogActions sx={{ width: '100%' }}>
        <Box display="flex" justifyContent="space-between" gap={2} width="100%">
          <FlexBox gap={2}>
            <ThemeButton color="BLUE_ICE" size="large" type="button" onClick={onClose}>
              Cancel
            </ThemeButton>
            {site.siteClusterId && (
              <>
                <ThemeButton
                  color="BLUE_ICE"
                  size="large"
                  type="button"
                  loading={loadingMutation}
                  onClick={handleRemoveFromCluster}
                >
                  Remove site from cluster
                </ThemeButton>
                <InlineHelperText
                  variant="INFO"
                  helperText="A site does not need to belong to an origin cluster."
                />
              </>
            )}
          </FlexBox>

          <ThemeButton
            color="YELLOW"
            size="large"
            type="submit"
            disabled={hasErrors || !selectedClusterId || selectedClusterId === site.siteClusterId}
            tooltip={hasErrors ? 'Please resolve the errors before saving' : undefined}
            loading={loadingMutation}
            onClick={handleChangeCluster}
          >
            Save selection
          </ThemeButton>
        </Box>
      </DialogActions>
    </DialogDefault>
  );
};

export default ManageSiteDialog;
